/** STK-352 recap-detail transition registry. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { usePreOrderModifyBatch, usePreOrderMutations } from "@stockline/web";
import { type PreOrder, PreOrderStatus } from "@/app/api/types/preOrder";
import { UUID } from "@/app/api/types";
import {
  preOrderKeys,
  useConfirmPreOrder,
  useRetryExport,
} from "@/query/hooks/usePreOrders";
import { useUserOptional } from "@/components/auth-provider";
import { usePartnerAccount } from "@/query/hooks/usePartners";
import { useConfirmWarningsCheck } from "../contexts/ConfirmWarningsContext";
import {
  extractErpErrorMessage,
  type ModifyBufferApi,
} from "../hooks/useModifyBuffer";
import { useModifyBufferStoreOptional } from "../stores/modifyBufferStore";
import { effectivePreOrderFlows } from "../utils/effectiveFlows";
import { useDisabledColumnsConfig } from "../hooks/useDisabledColumnsConfig";
import { buildSanitizeOps } from "../utils/sanitizeDisabledColumns";
import { hasAnyPendingAi } from "../services/recapService";
import { resolveAiLocally } from "../services/aiResolveLocally/index";
import { useRecapAiSuggestions } from "../hooks/useRecapAiSuggestions";
import {
  useAiPreOrderActions,
  type AiPreOrderActions,
} from "@/app/ai-pre-orders/hooks/useAiPreOrderActions";
import { confirmInFlight } from "../stores/confirmInFlightStore";
import { useAiInFlightSlice } from "../stores/aiInFlightStore";
import {
  buildConfirmHeaderUpdate,
  clearPendingHeaderWrite,
} from "../services/pending-header-writes";
import type { RecapTransitionId } from "./buttonTable";

/** POST 202 → SSE `export.status="in_progress"` gap. */
const FLICKER_GUARD_MS = 5000;

function getErrorCode(e: unknown): unknown {
  if (e && typeof e === "object" && "code" in e)
    return (e as { code: unknown }).code;
  return undefined;
}

/** Benign confirm/retry rejections the hook-level toast already surfaced. */
function isBenignConfirmCode(code: unknown): boolean {
  return (
    code === "export_in_progress" ||
    code === "already_in_flight" ||
    code === "export_already_succeeded"
  );
}

/** Commit any typed-but-unfocused cell value into the buffer before we read it. */
function blurActive(): void {
  const el = document.activeElement as HTMLElement | null;
  if (el && typeof el.blur === "function") el.blur();
}

/**
 * THE accept-AI-stay-pending gesture: resolve every pending AI signal via one
 * push-batch, no confirm/export. Single implementation shared by the
 * transition command below and `RecapModifyCancelSlot`'s table-header chip
 * (which runs its own `useAiPreOrderActions` instance rather than this hook).
 * `approveAll` stays on the row (its "navigate" is a scroll to the accepted
 * content) and toasts its own errors — the catch only silences the rethrow.
 */
export function runAcceptAiPending(
  aiActions: Pick<AiPreOrderActions, "approveAll">,
  preOrder: PreOrder,
): void {
  blurActive();
  void aiActions.approveAll(preOrder).catch(() => {});
}

export interface RecapTransitionsInput {
  preOrder: PreOrder;
  /** Published up from `<PreOrderSection>`; null for the first frame. */
  buffer: ModifyBufferApi | null;
  /** Focus-view delete (parent owns the mutation + post-delete nav). */
  onDelete?: () => void;
  /** Focus-view restore. */
  onRestore?: () => void;
}

export interface RecapTransitions {
  commands: Record<RecapTransitionId, () => void>;
  /** Any transition in flight — disables every action button. */
  busy: boolean;
  /** Confirm/export round-trip in flight (local optimistic + retry window). */
  isConfirming: boolean;
  /** Popover wiring for the failed export state. */
  failed: {
    orders: PreOrder[];
    onRetry: (orders: PreOrder[]) => void;
    isRetrying: boolean;
  };
  /** Popover wiring for the ill (sync-drift) state. */
  ill: {
    orders: PreOrder[];
    onHeal: () => void;
    isHealing: boolean;
    canHeal: boolean;
    isRetrying: boolean;
  };
  /** Blocked-partner confirmation dialog state (rendered by RecapActionBar). */
  blocked: {
    open: boolean;
    setOpen: (v: boolean) => void;
    onContinue: () => void;
  };
}

/**
 * Build the per-transition commands for one pre-order. Pure orchestration;
 * the renderer (`RecapActionBar`) decides which commands are reachable.
 */
export function useRecapTransitions({
  preOrder,
  buffer,
  onDelete,
  onRestore,
}: RecapTransitionsInput): RecapTransitions {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useUserOptional();
  const aiActions = useAiPreOrderActions(preOrder.id);
  // Approve/decline in-flight state from the SHARED per-PO store, not this
  // hook instance's aiActions flags: the table-header Accept AI chip runs its
  // own useAiPreOrderActions instance, and only the store sees its round-trip
  // (so the hero buttons spin, not just silently disable, during it).
  const aiInFlightState = useAiInFlightSlice(preOrder.id);
  const confirmMutation = useConfirmPreOrder();
  const retryExportMutation = useRetryExport();
  const { updatePreOrder } = usePreOrderMutations();
  const modifyBatch = usePreOrderModifyBatch();
  const confirmWarn = useConfirmWarningsCheck();
  const { rowIndex: aiRowIndex, stampResolutions } = useRecapAiSuggestions();
  const bufferStore = useModifyBufferStoreOptional();
  const disabledColumns = useDisabledColumnsConfig();

  const [localConfirming, setLocalConfirming] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [isHealing, setIsHealing] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const localConfirmingIdsRef = useRef<Set<string>>(new Set());
  const retryingIdsRef = useRef<Set<string>>(new Set());
  const healingIdsRef = useRef<Set<string>>(new Set());

  // This hook instance survives prev/next navigation, so transient flags must
  // belong to the currently focused order only.
  const [trackedId, setTrackedId] = useState(preOrder.id);
  const activeIdRef = useRef(preOrder.id);
  activeIdRef.current = preOrder.id;
  const setIfCurrent = useCallback((id: string, apply: () => void) => {
    if (activeIdRef.current === id) apply();
  }, []);
  if (trackedId !== preOrder.id) {
    const id = preOrder.id;
    setTrackedId(id);
    setLocalConfirming(localConfirmingIdsRef.current.has(id));
    setRetrying(retryingIdsRef.current.has(id));
    setIsHealing(healingIdsRef.current.has(id));
    setShowBlocked(false);
  }

  const partnerId = (preOrder.partner_id ?? null) as UUID | null;
  const { data: partnerAccount } = usePartnerAccount(partnerId);
  const isPartnerBlocked = partnerAccount?.is_blocked === true;

  const exportStatus = preOrder.pre_order_metadata?.export?.status ?? null;
  const isConfirming =
    localConfirming || retrying || exportStatus === "in_progress";

  // Mirror the optimistic confirm window into the shared store so the flows
  // table / comment block / header inputs lock the instant the click fires,
  // before the server's SSE `in_progress` lands. (The metadata-driven part is
  // read separately via useIsPreOrderConfirming.)
  useEffect(() => {
    const active = localConfirming || retrying;
    confirmInFlight.setConfirming(preOrder.id, active);
    return () => confirmInFlight.setConfirming(preOrder.id, false);
  }, [preOrder.id, localConfirming, retrying]);

  // Retry flicker-guard: hold the spinner until SSE moves export.status out of
  // "failed", or FLICKER_GUARD_MS elapses, so the button never flashes "failed"
  // for one frame between the 202 and the SSE arrival.
  useEffect(() => {
    if (!retrying) return;
    if (exportStatus !== "failed" && exportStatus !== "in_progress") {
      retryingIdsRef.current.delete(preOrder.id);
      setRetrying(false);
      return;
    }
    const timer = setTimeout(() => {
      retryingIdsRef.current.delete(preOrder.id);
      setRetrying(false);
    }, FLICKER_GUARD_MS);
    return () => clearTimeout(timer);
  }, [retrying, exportStatus, preOrder.id]);

  // --- accept + confirm (the unified "…and confirm" / "Accept" / "Confirm") ---

  /**
   * Phase 1: persist edits + accept pending AI. Phase 2: for a not-yet-
   * confirmed row, trigger the first ERP export. A CONFIRMED/EXPORTED parent
   * re-exports inside phase 1 (flushSave / AI-resolve push-batch keeps the
   * status, so the server re-runs the export), so phase 2 is skipped.
   */
  const runAcceptThenExport = useCallback(
    async (wasConfirmed: boolean) => {
      const transitionPreOrderId = preOrder.id;
      localConfirmingIdsRef.current.add(transitionPreOrderId);
      setLocalConfirming(true);
      try {
        // Phase 1 — save edits + accept AI. Force a FOREGROUND flush: the
        // default background push would resolve after only the optimistic
        // apply, letting phase 2's confirm/export POST race ahead of the
        // server-committed save (and clear the buffer before the save lands).
        if (buffer?.isModifying) {
          bufferStore?.setLastError(preOrder.id, null);
          await buffer.flushSave({ background: false });
          // flushSave swallows errors into the buffer store; abort the export
          // if the save didn't commit.
          if (
            bufferStore &&
            bufferStore.getSlice(preOrder.id).lastError !== null
          ) {
            return;
          }
        } else if (hasAnyPendingAi(preOrder)) {
          // Non-modify accept (no buffer edits). Resolve + push directly — the
          // approveAll hook navigates to the accepted path, which would unmount
          // the focus page mid-gesture; the focus row stays put instead.
          const resolved = resolveAiLocally(
            { userId: user?.id ?? "", resolvedAt: new Date().toISOString() },
            preOrder,
            { preOrderId: preOrder.id, flowChanges: [] },
            "accept",
            aiRowIndex,
          );
          await modifyBatch.pushBatch(resolved.batch);
          // Fire-and-forget: the column writes already committed via push-batch.
          // Awaiting here would let a stamp failure fall into the export catch
          // below — surfacing a misleading "export failed" toast and skipping
          // phase-2 confirm even though the accept landed. Stamping is
          // non-fatal (best-effort; the next sync reconciles), matching every
          // other call site.
          void stampResolutions(resolved.resolutions);
        }

        if (wasConfirmed) return; // re-export already fired in phase 1

        // Nullify disabled-column values before exporting so invisible
        // data never leaks to ERPs.
        const flows = effectivePreOrderFlows(
          preOrder,
          bufferStore?.getSlice(preOrder.id),
        );
        const sanitizeOps = buildSanitizeOps(flows, disabledColumns);
        if (sanitizeOps.length > 0) {
          await modifyBatch.pushBatch({
            preOrderId: preOrder.id,
            flowChanges: sanitizeOps,
          });
        }

        // Phase 2 — first export of the now-PENDING row. Queued AFTER phase 1
        // in the ordered sync queue, so the server promotes/saves first.
        //
        // Fold any header edit made in this same gesture (e.g. a just-cleared
        // comment) into the confirm push-batch so the server applies it in the
        // SAME transaction as the export. That edit also rides a separate
        // change-queue push (useUpdatePreOrder) which the confirm would
        // otherwise race — exporting the stale value and repainting it on
        // reconcile (STK-991). A microtask yield lets the blur-triggered
        // dispatch that fired this gesture register its value first.
        await Promise.resolve();
        const headerUpdate = buildConfirmHeaderUpdate(preOrder.id);
        await confirmMutation.mutateAsync({
          preOrderId: preOrder.id,
          ...(headerUpdate ? { preOrderUpdate: headerUpdate } : {}),
        });
        // Only reached when the confirm resolved. Drop the folded patch; on a
        // failure it stays registered so a retry re-folds it (the change-queue
        // push is the durable backstop meanwhile).
        clearPendingHeaderWrite(preOrder.id);
      } catch (e) {
        if (!isBenignConfirmCode(getErrorCode(e))) {
          toast.error(t("ordersRecap.exportFailed"), {
            description: `${preOrder.partner?.name || preOrder.id}: ${extractErpErrorMessage(e)}`,
            duration: 10000,
          });
        }
      } finally {
        localConfirmingIdsRef.current.delete(transitionPreOrderId);
        setIfCurrent(transitionPreOrderId, () => setLocalConfirming(false));
      }
    },
    [
      aiRowIndex,
      buffer,
      bufferStore,
      disabledColumns,
      preOrder,
      user,
      modifyBatch,
      stampResolutions,
      confirmMutation,
      t,
      setIfCurrent,
    ],
  );

  const acceptAndConfirm = useCallback(() => {
    blurActive();
    const wasConfirmed =
      preOrder.status === PreOrderStatus.CONFIRMED ||
      preOrder.status === PreOrderStatus.EXPORTED;
    const launch = () => {
      // Blocked-partner only gates the FIRST export (M3 stop-client / credit
      // limit); a re-export of an already-exported order is the operator's
      // explicit recovery, so it isn't re-gated.
      if (!wasConfirmed && isPartnerBlocked) {
        setShowBlocked(true);
        return;
      }
      void runAcceptThenExport(wasConfirmed);
    };
    if (wasConfirmed) {
      launch();
      return;
    }
    const flows = effectivePreOrderFlows(
      preOrder,
      bufferStore?.getSlice(preOrder.id),
    );
    // One aggregated pre-confirm gate: required custom fields + zero quantity +
    // (purchase-only) zero price. The dialog names each offending line + field.
    const isPurchase = preOrder.category === 1;
    if (confirmWarn) {
      confirmWarn({ flows, isPurchase }, launch);
    } else {
      launch();
    }
  }, [
    preOrder,
    isPartnerBlocked,
    confirmWarn,
    bufferStore,
    runAcceptThenExport,
  ]);

  // --- save modifications WITHOUT confirming (flag-OFF header escape) --------

  const saveModifications = useCallback(async () => {
    if (!buffer) return;
    blurActive();
    bufferStore?.setLastError(preOrder.id, null);
    // flushSave persists edits + accepts AI and stays local for a PENDING /
    // AI_PROPOSAL row (no export). The Save button is never offered on a
    // CONFIRMED row, so this never silently re-exports.
    await buffer.flushSave();
  }, [buffer, bufferStore, preOrder.id]);

  // --- AI accept, stay pending ----------------------------------------------

  // One-click resolve of every pending AI signal WITHOUT the export phase —
  // for orders the user parks on hold (their ERP order type can't be modified
  // once entered, so accept must not trigger a confirm). Delegates to the
  // shared `runAcceptAiPending` so this command and the table-header chip
  // can never diverge.
  const acceptAiPending = useCallback(
    () => runAcceptAiPending(aiActions, preOrder),
    [aiActions, preOrder],
  );

  // --- AI reject ------------------------------------------------------------

  const rejectAi = useCallback(() => {
    // Stay on the order and let the new state render in place:
    //  - whole-PO AI_PROPOSAL → `declineAll` soft-deletes it (deleted_at set) →
    //    the bar re-resolves to the cancelled lifecycle → Restore-only.
    //  - PENDING / CONFIRMED + AI → the suggestions flip to declined, the order
    //    keeps its status, and the bar re-resolves to the no-AI case.
    // The optimistic cache update drives the transition; no navigation.
    void aiActions.declineAll(preOrder).catch(() => {});
  }, [aiActions, preOrder]);

  // --- unconfirm (flag OFF) ------------------------------------------------

  // Offline-first: flip the pre-order back to PENDING (0) straight into Dexie
  // via the generic update op, the same path delete / restore ride. The recap
  // renders from Dexie, so the previous REST-only `useBatchUpdatePreOrderStatus`
  // (cache-only, no Dexie write) appeared to hang — the badge never changed
  // because the live row was untouched. `status` is a lifecycle field exempt
  // from the header lock (`assertHeaderEditable`), so the CONFIRMED→PENDING
  // downgrade is accepted on push. Editability re-derives from the PENDING
  // status; the user can re-confirm (which re-exports) from there.
  const unconfirm = useCallback(() => {
    void updatePreOrder(preOrder.id, {
      status: PreOrderStatus.PENDING,
    }).catch(() => {
      toast.error(
        t("ordersRecap.detailActions.unconfirmFailed", "Unconfirm failed"),
      );
    });
  }, [updatePreOrder, preOrder.id, t]);

  // --- retry export (failed) ----------------------------------------------

  const retryExport = useCallback(() => {
    if (exportStatus !== "failed" || retrying) return;
    const transitionPreOrderId = preOrder.id;
    retryingIdsRef.current.add(transitionPreOrderId);
    setRetrying(true);
    void (async () => {
      try {
        const hasReference = Boolean(
          preOrder.pre_order_metadata?.export?.reference,
        );
        if (!hasReference) {
          await confirmMutation.mutateAsync({ preOrderId: preOrder.id });
        } else {
          try {
            await retryExportMutation.mutateAsync({ preOrderId: preOrder.id });
          } catch (e) {
            if (getErrorCode(e) === "cannot_retry_no_reference") {
              await confirmMutation.mutateAsync({ preOrderId: preOrder.id });
            } else {
              throw e;
            }
          }
        }
      } catch (e) {
        if (!isBenignConfirmCode(getErrorCode(e))) {
          retryingIdsRef.current.delete(transitionPreOrderId);
          setIfCurrent(transitionPreOrderId, () => setRetrying(false));
          toast.error(t("ordersRecap.exportFailed"), {
            description: `${preOrder.partner?.name || preOrder.id.slice(0, 8)}: ${extractErpErrorMessage(e)}`,
            duration: 10000,
          });
        }
      }
    })();
  }, [
    exportStatus,
    retrying,
    preOrder,
    confirmMutation,
    retryExportMutation,
    t,
    setIfCurrent,
  ]);

  // --- heal ill (sync drift) ----------------------------------------------

  const sh = preOrder.pre_order_metadata?.sync_health;
  const canHeal =
    sh?.ill === true &&
    (sh.reason === "orphan_flows" || sh.reason === "m3_order_locked");

  const healIll = useCallback(() => {
    if (isHealing || !sh || sh.ill !== true) return;
    const transitionPreOrderId = preOrder.id;
    healingIdsRef.current.add(transitionPreOrderId);
    setIsHealing(true);
    void (async () => {
      try {
        if (sh.reason === "m3_order_locked") {
          // Operator closed the M3 order → re-fire export via confirm (empty
          // diff); server detects the existing reference and routes the update.
          await confirmMutation.mutateAsync({ preOrderId: preOrder.id });
        } else if (sh.reason === "orphan_flows") {
          const orphanIds = sh.orphan_flow_ids ?? [];
          if (orphanIds.length > 0) {
            await modifyBatch.pushBatch({
              preOrderId: preOrder.id,
              flowChanges: orphanIds.map((id) => ({
                op: "update" as const,
                id,
                data: {},
              })),
            });
          }
        }
        toast.success(
          t("ordersRecap.syncHealthHealOk", {
            defaultValue: "Réexport en cours — sync M3",
            count: 1,
          }),
        );
      } catch (e) {
        if (!isBenignConfirmCode(getErrorCode(e))) {
          toast.error(
            t("ordersRecap.syncHealthHealFailed", {
              defaultValue: "Réexport échoué",
            }),
            { description: extractErpErrorMessage(e), duration: 10000 },
          );
        }
      } finally {
        healingIdsRef.current.delete(transitionPreOrderId);
        setIfCurrent(transitionPreOrderId, () => setIsHealing(false));
        queryClient.invalidateQueries({
          queryKey: [...preOrderKeys.all, "recap"],
        });
        queryClient.invalidateQueries({
          queryKey: preOrderKeys.detail(preOrder.id as UUID),
        });
      }
    })();
  }, [
    isHealing,
    sh,
    preOrder.id,
    confirmMutation,
    modifyBatch,
    queryClient,
    t,
    setIfCurrent,
  ]);

  // --- simple buffer / parent-owned commands -------------------------------

  const cancelModifications = useCallback(() => {
    buffer?.cancelModify();
  }, [buffer]);

  const enterModify = useCallback(() => {
    buffer?.enterModify();
  }, [buffer]);

  const deleteOrder = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  const restore = useCallback(() => {
    onRestore?.();
  }, [onRestore]);

  const commands: Record<RecapTransitionId, () => void> = {
    acceptAndConfirm,
    acceptAiPending,
    saveModifications: () => void saveModifications(),
    rejectAi,
    cancelModifications,
    enterModify,
    deleteOrder,
    unconfirm,
    retryExport,
    healIll,
    restore,
  };

  const busy =
    localConfirming ||
    retrying ||
    isHealing ||
    aiInFlightState.isApprovingAi ||
    aiInFlightState.isDecliningAi ||
    // unconfirm is an instant offline-first Dexie write — no pending state.
    (buffer?.isFlushing ?? false);

  return {
    commands,
    busy,
    isConfirming,
    failed: {
      orders: [preOrder],
      onRetry: () => retryExport(),
      isRetrying: retrying,
    },
    ill: {
      orders: [preOrder],
      onHeal: healIll,
      isHealing,
      canHeal,
      isRetrying: retrying,
    },
    blocked: {
      open: showBlocked,
      setOpen: setShowBlocked,
      onContinue: () => {
        setShowBlocked(false);
        // Blocked only fires for a not-yet-confirmed first export.
        void runAcceptThenExport(false);
      },
    },
  };
}
